// Statistics shell: owns the date range + data fetching and composes the section
// components. Phase 1 refactor of the original single-file StatisticsPage — same layout
// and behaviour, just split into ./statistics/* for future extension (per-user activity,
// work calendar). New sections drop in here without touching the existing ones.
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch, API } from '../api';
import {
  DatePreset, DATE_PRESETS, Session, DailyRow, ProblematicOrder,
  weekRange, monthRange, last30Range, allTimeRange,
} from './shared';
import OverviewSection from './OverviewSection';
import PpRoundsTable from './PpRoundsTable';
import PickerPace from './PickerPace';
import DailyTable from './DailyTable';
import ProblematicTable from './ProblematicTable';
import PerUserTab from './PerUserTab';
import UnclaimedCodStats from './UnclaimedCodStats';

type StatView = 'overview' | 'peruser' | 'unclaimedcod';

export default function StatisticsPage() {
  const role = localStorage.getItem('wms_role') || 'staff';
  const isManager = role === 'admin' || role === 'manager';
  const isAdmin = role === 'admin';

  const [view, setView]             = useState<StatView>('overview');
  const [preset, setPreset]         = useState<DatePreset>('last30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [rounds,     setRounds]     = useState<Session[]>([]);
  const [daily,      setDaily]      = useState<DailyRow[]>([]);
  const [problematic,setProblematic]= useState<ProblematicOrder[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [exporting,  setExporting]  = useState(false);
  const [retSpeed,   setRetSpeed]   = useState<any | null>(null);

  const getRange = useCallback(() => {
    if (preset === 'week')   return weekRange();
    if (preset === 'month')  return monthRange();
    if (preset === 'all')    return allTimeRange();
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return last30Range();
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    const { from, to } = getRange();
    if (!from || !to) return;
    setLoading(true);
    try {
      const qs = `from_date=${from}&to_date=${to}`;
      const [sessData, dailyData] = await Promise.all([
        authFetch(`${API}/statistics/sessions?${qs}`).then(r => r.json()),
        authFetch(`${API}/statistics/daily?${qs}`).then(r => r.json()),
      ]);
      setRounds(sessData.sessions  || []);
      setDaily(dailyData.daily       || []);
      authFetch(`${API}/returns/stats/speed?${qs}`).then(r => r.json()).then(setRetSpeed).catch(() => {});
      if (isManager) {
        const probData = await authFetch(`${API}/statistics/problematic?${qs}`).then(r => r.json());
        setProblematic(probData.orders || []);
      }
    } catch (e) {
      console.error('Stats load error:', e);
    }
    setLoading(false);
  }, [getRange, isManager]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    const { from, to } = getRange();
    if (!from || !to) return;
    setExporting(true);
    try {
      const resp = await authFetch(`${API}/statistics/export?from_date=${from}&to_date=${to}`);
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `wms_stats_${from}_to_${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const presets = DATE_PRESETS;

  return (
    <div className="p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white">Statistics</h1>
          <p className="text-gray-500 text-sm mt-0.5">Performance overview</p>
        </div>
        {view === 'overview' && (
          <button onClick={exportXlsx} disabled={exporting || loading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors">
            {exporting ? 'Exporting…' : '↓ Export .xlsx'}
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      {isAdmin && (
        <div className="flex gap-1 border-b border-gray-800 mb-6">
          {([['overview', 'Overview'], ['peruser', 'Per-User'], ['unclaimedcod', 'Unclaimed COD']] as [StatView, string][]).map(([key, label]) => (
            <button key={key} onClick={() => setView(key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                view === key ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'
              }`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {view === 'overview' && (
        <>
          {/* Date range */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {presets.map(p => (
              <button key={p.key} onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  preset === p.key ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}>
                {p.label}
              </button>
            ))}
            {preset === 'custom' && (
              <div className="flex items-center gap-2 ml-1">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="bg-gray-800 text-gray-200 text-sm rounded px-2 py-1 border border-gray-700 focus:outline-none focus:border-blue-500" />
                <span className="text-gray-600">—</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="bg-gray-800 text-gray-200 text-sm rounded px-2 py-1 border border-gray-700 focus:outline-none focus:border-blue-500" />
              </div>
            )}
            {loading && <span className="text-gray-600 text-sm ml-1">Loading…</span>}
          </div>

          <OverviewSection rounds={rounds} problematic={problematic} retSpeed={retSpeed} isManager={isManager} />
          <PpRoundsTable rounds={rounds} isManager={isManager} />
          <PickerPace />
          <DailyTable daily={daily} isManager={isManager} />
          {isManager && problematic.length > 0 && <ProblematicTable problematic={problematic} />}
        </>
      )}

      {view === 'peruser' && isAdmin && <PerUserTab />}
      {view === 'unclaimedcod' && isAdmin && <UnclaimedCodStats />}
    </div>
  );
}
